import { useEffect, useState } from "react";
import AdminLayout from "@/pages/admin/AdminLayout";
import api from "@/lib/api";
import { toast } from "sonner";
import { TrendingUp, Eye, MessageSquare, MousePointerClick, Star, Image, Monitor, ExternalLink, Loader2, Send } from "lucide-react";

const AdminTurnkeyAnalytics = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [sendingDigest, setSendingDigest] = useState(false);

  const load = () => {
    setLoading(true);
    const params = new URLSearchParams();
    if (dateFrom) params.set("date_from", dateFrom);
    if (dateTo) params.set("date_to", dateTo);
    api.get(`/admin/turnkey-marketplace-analytics?${params.toString()}`)
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  };

  // Reload whenever the date range changes. `load` is intentionally NOT in the
  // dep array — it's re-created every render (fresh reference) which would
  // otherwise put us in an infinite loop (setState → new load → effect fires).
  useEffect(() => { load(); }, [dateFrom, dateTo]);

  const sendDigestNow = async () => {
    setSendingDigest(true);
    try {
      const { data: r } = await api.post("/admin/turnkey-inquiries/send-follow-up-digest");
      if (r.sent) toast.success(`Digest sent — ${r.count} inquiry follow-up${r.count !== 1 ? "s" : ""}.`);
      else toast(r.reason === "nothing_due" ? "Nothing due today — no digest sent." : `Skipped: ${r.reason}`);
    } catch (err) { toast.error("Digest send failed"); }
    finally { setSendingDigest(false); }
  };

  const t = data?.totals || {};
  const funnel = [
    { label: "Marketplace Card Views", value: t.total_views || 0, Icon: Eye },
    { label: "Gallery Image Clicks", value: t.total_gallery_clicks || 0, Icon: Image },
    { label: "Website Preview Clicks", value: t.total_preview_clicks || 0, Icon: Monitor },
    { label: "Live Demo Clicks", value: t.total_demo_clicks || 0, Icon: ExternalLink },
    { label: "Inquire Button Clicks", value: t.total_inquire_clicks || 0, Icon: MousePointerClick },
    { label: "Inquiries Submitted", value: t.total_inquiries || 0, Icon: MessageSquare },
  ];

  const max = Math.max(1, ...funnel.map((f) => f.value));

  return (
    <AdminLayout>
      <div className="flex items-center justify-between mb-6 flex-wrap gap-4">
        <div>
          <div className="ft-eyebrow mb-2">Turnkey Marketplace</div>
          <h1 className="text-3xl font-bold tracking-tight" data-testid="admin-turnkey-analytics-title">
            Command Center · Analytics
          </h1>
          <p className="text-[var(--ft-muted)] mt-2 max-w-2xl">
            Executive dashboard for the Turnkey Marketplace. View brand-level performance, funnel
            drop-off, and monthly trends.
          </p>
        </div>
        <button onClick={sendDigestNow} disabled={sendingDigest} className="ft-btn-secondary" data-testid="analytics-send-digest">
          {sendingDigest ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />} Send Follow-Up Digest Now
        </button>
      </div>

      {/* DATE FILTERS */}
      <div className="ft-card !p-4 flex items-center gap-3 mb-6 flex-wrap">
        <span className="text-xs uppercase tracking-[0.16em] font-semibold text-[var(--ft-muted)]">Inquiry Date Range</span>
        <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="ft-input !py-1.5 max-w-[180px]" data-testid="analytics-date-from" />
        <span className="text-[var(--ft-muted)]">→</span>
        <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="ft-input !py-1.5 max-w-[180px]" data-testid="analytics-date-to" />
        {(dateFrom || dateTo) && (
          <button onClick={() => { setDateFrom(""); setDateTo(""); }} className="text-xs text-[var(--ft-muted)] hover:text-[var(--ft-black)]">Clear</button>
        )}
      </div>

      {loading ? (
        <div className="ft-card fx-skeleton h-96" />
      ) : !data ? (
        <div className="ft-card text-center py-16">
          <TrendingUp size={40} className="mx-auto mb-4 text-[var(--ft-muted)]" />
          <h3 className="text-lg font-semibold">Analytics unavailable.</h3>
        </div>
      ) : (
        <>
          {/* TOP KPIs */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6" data-testid="analytics-kpis">
            {[
              { label: "Total Views", value: t.total_views, Icon: Eye },
              { label: "Total Inquiries", value: t.total_inquiries, Icon: MessageSquare },
              { label: "Conversion Rate", value: `${t.overall_conversion_rate || 0}%`, Icon: TrendingUp },
              { label: "Most Viewed", value: t.most_viewed || "—", Icon: Eye, small: true },
              { label: "Most Inquired", value: t.most_inquired || "—", Icon: MousePointerClick, small: true },
            ].map((k) => (
              <div key={k.label} className="ft-card !p-4">
                <div className="flex items-center justify-between">
                  <div className="text-[0.6rem] uppercase tracking-[0.22em] text-[var(--ft-muted)] font-semibold">{k.label}</div>
                  <k.Icon size={14} className="text-[var(--ft-gold)]" />
                </div>
                <div className={`font-bold mt-1 tracking-tight text-[var(--ft-black)] ${k.small ? "text-sm" : "text-2xl"}`}>{k.value}</div>
              </div>
            ))}
          </div>

          {/* FUNNEL VISUALIZATION */}
          <div className="ft-card mb-6" data-testid="analytics-funnel">
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="ft-eyebrow">Buyer Funnel</div>
                <h3 className="text-lg font-semibold mt-1">Marketplace → Inquiry</h3>
              </div>
            </div>
            <div className="space-y-2">
              {funnel.map((f) => {
                const pct = Math.round((f.value / max) * 100);
                return (
                  <div key={f.label}>
                    <div className="flex items-center justify-between text-xs mb-1">
                      <span className="flex items-center gap-2 text-[var(--ft-charcoal)] font-semibold">
                        <f.Icon size={12} className="text-[var(--ft-gold)]" /> {f.label}
                      </span>
                      <span className="text-[var(--ft-muted)] font-mono">{f.value}</span>
                    </div>
                    <div className="h-6 rounded-md bg-[var(--ft-light-gray)] overflow-hidden">
                      <div
                        className="h-full flex items-center justify-end pr-2 text-[0.65rem] text-white font-semibold transition-all"
                        style={{
                          width: `${Math.max(2, pct)}%`,
                          background: "linear-gradient(90deg, var(--ft-gold) 0%, #b58a2f 100%)",
                        }}
                      >
                        {pct > 10 ? `${pct}%` : ""}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* FEATURED PERFORMANCE */}
          {t.featured_count > 0 && (
            <div className="ft-card mb-6" data-testid="analytics-featured">
              <div className="flex items-center gap-2 mb-3">
                <Star size={16} fill="var(--ft-gold)" color="var(--ft-gold)" />
                <div className="ft-eyebrow">Featured Brand Performance</div>
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <div className="text-[0.6rem] uppercase tracking-[0.22em] text-[var(--ft-muted)] font-semibold">Featured Brands</div>
                  <div className="text-2xl font-bold mt-1">{t.featured_count}</div>
                </div>
                <div>
                  <div className="text-[0.6rem] uppercase tracking-[0.22em] text-[var(--ft-muted)] font-semibold">Combined Views</div>
                  <div className="text-2xl font-bold mt-1">{t.featured_views}</div>
                </div>
                <div>
                  <div className="text-[0.6rem] uppercase tracking-[0.22em] text-[var(--ft-muted)] font-semibold">Combined Inquiries</div>
                  <div className="text-2xl font-bold mt-1">{t.featured_inquiries}</div>
                </div>
              </div>
            </div>
          )}

          {/* PER-BRAND COMPARISON */}
          <div className="ft-card !p-0 overflow-x-auto mb-6" data-testid="analytics-per-brand">
            <table className="w-full text-sm min-w-[900px]">
              <thead className="bg-[var(--ft-light-gray)] text-[0.7rem] uppercase tracking-[0.16em] font-semibold text-[var(--ft-charcoal)]">
                <tr>
                  <th className="text-left p-4">Brand</th>
                  <th className="text-right p-4">Views</th>
                  <th className="text-right p-4">Gallery</th>
                  <th className="text-right p-4">Preview</th>
                  <th className="text-right p-4">Demo</th>
                  <th className="text-right p-4">Inquire</th>
                  <th className="text-right p-4">Inquiries</th>
                  <th className="text-right p-4">Conv %</th>
                </tr>
              </thead>
              <tbody>
                {data.rows.map((r) => (
                  <tr key={r.slug} className="border-t border-[var(--ft-border)] hover:bg-[var(--ft-light-gray)]" data-testid={`analytics-row-${r.slug}`}>
                    <td className="p-4">
                      <div className="flex items-center gap-2">
                        {r.is_featured && <Star size={14} fill="var(--ft-gold)" color="var(--ft-gold)" />}
                        <div>
                          <div className="font-semibold text-[var(--ft-black)]">{r.brand_name}</div>
                          <div className="text-xs text-[var(--ft-muted)]">{r.status}</div>
                        </div>
                      </div>
                    </td>
                    <td className="p-4 text-right font-mono">{r.views}</td>
                    <td className="p-4 text-right font-mono text-[var(--ft-muted)]">{r.gallery_clicks}</td>
                    <td className="p-4 text-right font-mono text-[var(--ft-muted)]">{r.preview_clicks}</td>
                    <td className="p-4 text-right font-mono text-[var(--ft-muted)]">{r.demo_clicks}</td>
                    <td className="p-4 text-right font-mono text-[var(--ft-muted)]">{r.inquire_clicks}</td>
                    <td className="p-4 text-right font-mono font-semibold text-[var(--ft-black)]">{r.inquiries}</td>
                    <td className="p-4 text-right font-mono font-semibold" style={{ color: r.conversion_rate > 5 ? "var(--ft-gold)" : "var(--ft-muted)" }}>
                      {r.conversion_rate}%
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* MONTHLY TREND */}
          {data.monthly_trend && data.monthly_trend.length > 0 && (
            <div className="ft-card" data-testid="analytics-monthly">
              <div className="ft-eyebrow mb-3">Monthly Inquiry Trend</div>
              <div className="flex items-end gap-3 h-32">
                {data.monthly_trend.map((m) => {
                  const maxM = Math.max(1, ...data.monthly_trend.map((x) => x.inquiries));
                  const h = Math.round((m.inquiries / maxM) * 100);
                  return (
                    <div key={m.month} className="flex-1 flex flex-col items-center gap-2">
                      <div className="text-xs font-mono text-[var(--ft-charcoal)] font-semibold">{m.inquiries}</div>
                      <div className="w-full rounded-t bg-gradient-to-t from-[var(--ft-gold)] to-[#b58a2f]" style={{ height: `${h}%` }} />
                      <div className="text-[0.65rem] uppercase tracking-[0.14em] text-[var(--ft-muted)] font-semibold">{m.month}</div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </>
      )}
    </AdminLayout>
  );
};

export default AdminTurnkeyAnalytics;
